import { DocSubTypeEnum, PartySubTypeEnum, PartyTypeEnum, } from '@zurich/api/party';
import { createStringEnumUtils, UnknownType } from '@zurich/common/utils';
import { PartyCountryIsoCode, PartyDiplomaticStatus } from './notMigratedYet';
export const partyDocumentDocSubTypeUtils = createStringEnumUtils(DocSubTypeEnum, {
    unknownType: UnknownType.STRING,
    translationPrefix: 'party:document.docSubType',
});
export const partyDocumentDocSubTypeOptions = (t, partyType, partySubType, modificationReason, partyModified) => {
    const options = partyDocumentDocSubTypeUtils
        .createOptions(t)
        .filter((doc) => {
        if (doc.value === DocSubTypeEnum.LEGI) {
            return ((partyType === PartyTypeEnum.J || partyType === PartyTypeEnum.N) &&
                (partySubType === PartySubTypeEnum.PRIVATPERSON ||
                    partySubType === PartySubTypeEnum.UNTERNEHMEN));
        }
        return (partyType === PartyTypeEnum.N &&
            (partySubType === PartySubTypeEnum.PRIVATPERSON ||
                partySubType === PartySubTypeEnum.EINZELUNTERNEHMER));
    });
    if (!modificationReason || !partyModified) {
        return options;
    }
    return options.filter((option) => [
        DocSubTypeEnum.PERS,
        DocSubTypeEnum.REIS,
        DocSubTypeEnum.AUFH,
        DocSubTypeEnum.KONV,
        DocSubTypeEnum.LEGI,
        ...getpartyModifiedDocSubtypes(modificationReason, partyModified),
    ].includes(option.value));
};
const getpartyModifiedDocSubtypes = (modificationReason, partyModified) => {
    switch (true) {
        case modificationReason === ModificationReason.NAMESAENDERUNG:
            return [DocSubTypeEnum.NAAE];
        case modificationReason === ModificationReason.DATENKORREKTUR &&
            partyModified === 'dateOfBirth':
            return [DocSubTypeEnum.GEBU];
        case modificationReason === ModificationReason.HEIRAT:
            return [DocSubTypeEnum.HEIR];
        case modificationReason === ModificationReason.DATENKORREKTUR:
            return [DocSubTypeEnum.FUEH];
        default:
            return [];
    }
};
export const createAttachmentList = (documents, docSubType, docType, t) => documents.map((document, idx) => ({
    key: document.externalKeyValue ?? idx,
    title: `${partyDocumentDocSubTypeUtils.getTranslatedValue(t, docSubType)}${documents.length > 1 ? `-${idx + 1}` : ''}`,
    href: `/api/document/elak/${document.externalKeyValue}?doctype=KOR`, // FIXME: should be IDN || KOR -> ${docType} . But IDN does not supported for now
}));
export const documentTypeSelector = (docSubType) => ({
    isDriversLicense: docSubType && docSubType === DocSubTypeEnum.FUEH,
    isPers: docSubType && docSubType === DocSubTypeEnum.PERS,
    isLegitimationCard: docSubType && docSubType === DocSubTypeEnum.LEGI, // DIPL is LEGI
    isIdentityDocument: docSubType &&
        [
            DocSubTypeEnum.REIS,
            DocSubTypeEnum.AUFH,
            DocSubTypeEnum.KONV,
            DocSubTypeEnum.PERS,
        ].includes(docSubType),
    isCertificate: docSubType &&
        [
            DocSubTypeEnum.GEBU,
            DocSubTypeEnum.HEIR,
            DocSubTypeEnum.NAAE,
            DocSubTypeEnum.SCHE,
        ].includes(docSubType),
});
export const createDocumentRow = (document, t, formatDate, formatTime) => {
    const { isIdentityDocument, isLegitimationCard, isPers } = documentTypeSelector(document.docSubType);
    return [
        ...('driversLicenseNumber' in document
            ? [
                {
                    label: t('party:document.driversLicenseNumber'),
                    value: document.driversLicenseNumber,
                },
            ]
            : []),
        ...('identityDocumentNumber' in document && isIdentityDocument
            ? [
                {
                    label: t(`party:document.identityDocumentNumber${document.docSubType === DocSubTypeEnum.PERS
                        ? DocSubTypeEnum.PERS
                        : DocSubTypeEnum.REIS}`),
                    value: document.identityDocumentNumber,
                },
            ]
            : []),
        ...('dDocNumber' in document && isLegitimationCard
            ? [
                {
                    label: t('party:document.dDocNumber'),
                    value: document.dDocNumber,
                },
                {
                    label: t('party:document.dDocSince'),
                    value: formatDate(document.dDocSince),
                },
                {
                    label: t('party:document.dDocUntil'),
                    value: formatDate(document.dDocUntil),
                },
                {
                    label: t('party:document.diplomaticStatus'),
                    value: PartyDiplomaticStatus.getTranslatedValue(document.diplomaticStatus, t),
                },
            ]
            : []),
        ...('issueDate' in document && !isLegitimationCard
            ? [
                {
                    label: t('party:document.issueDate'),
                    value: formatDate(document.issueDate),
                },
            ]
            : []),
        ...('expiryDate' in document && (isIdentityDocument || isPers)
            ? [
                {
                    label: t('party:document.expiryDate'),
                    value: document.expiryDate && formatDate(document.expiryDate),
                },
            ]
            : []),
        ...('countryOfIssue' in document && !isLegitimationCard
            ? [
                {
                    label: t('party:document.countryOfIssue'),
                    value: PartyCountryIsoCode.getTranslatedValue(document.countryOfIssue, t),
                },
            ]
            : []),
        ...('issuingAuthority' in document && !isLegitimationCard
            ? [
                {
                    label: t('party:document.issuingAuthority'),
                    value: document.issuingAuthority,
                },
            ]
            : []),
        {
            label: t('party:document.createdAt'),
            value: `${formatDate(document.createdAt)}, ${formatTime(document.createdAt)}`,
        },
    ];
};
const partyModifiedKeys = [
    'lastName',
    'firstName',
    'dateOfBirth',
];
export var ModificationReason;
(function (ModificationReason) {
    ModificationReason["NAMESAENDERUNG"] = "NAMESAENDERUNG";
    ModificationReason["DATENKORREKTUR"] = "DATENKORREKTUR";
    ModificationReason["HEIRAT"] = "HEIRAT";
    ModificationReason["SCHEIDUNG"] = "SCHEIDUNG";
})(ModificationReason || (ModificationReason = {}));
export const partyRelevantFieldModified = (keys) => partyModifiedKeys.find((key) => keys.includes(key));
export const partymodificationReasoneUtils = createStringEnumUtils(ModificationReason, {
    unknownType: UnknownType.STRING,
    translationPrefix: 'party:document.modificationReason',
});
export const getMdodificationReasonOptions = (modifiedKey, t) => {
    const options = partymodificationReasoneUtils.createOptions(t);
    switch (modifiedKey) {
        case 'lastName':
            return options;
        case 'firstName':
            return options.filter((option) => [
                ModificationReason.NAMESAENDERUNG,
                ModificationReason.DATENKORREKTUR,
            ].includes(option.value));
        case 'dateOfBirth':
            return options.filter((option) => [ModificationReason.DATENKORREKTUR].includes(option.value));
        default:
            return options;
    }
};
